// src/components/common/Header.jsx
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { Menu, X, MapPin, Sun, Moon, Monitor, LogOut } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/context/ThemeContext";
import UserDropdown from "./UserDropdown"; // Import the new dropdown
import Button from "./Button";
import { logoutOrganizer } from "@/store/slices/organizerSlice";
import { logoutSponsor } from "@/store/slices/sponsorSlice";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated: isUserAuth } = useAuth();
  const { isAuthenticated: isOrganizerAuth } = useSelector((state) => state.organizer);
  const { isAuthenticated: isSponsorAuth } = useSelector((state) => state.sponsor);
  const { theme, cycleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Determine which portal we are currently on based on the URL
  const isOnOrganizerPortal = location.pathname.startsWith("/organizer");
  const isOnSponsorPortal = location.pathname.startsWith("/sponsor");

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-900 shadow-lg z-50 transition-colors duration-300">
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center py-4">
          {/* Responsive Logo */}
          <Link to="/" className="flex items-center space-x-2 md:space-x-3">
            <img
              src={"/KOSET-LOGO-WITH-TM-1.png"}
              alt="Koset Logo"
              className="h-8 w-auto md:h-10" // Smaller on mobile, larger on desktop
            />
            <span className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white transition-colors duration-300">
              Koset
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {["/", "/events", "/leaderboard", "/about"].map((path) => (
              <Link
                key={path}
                to={path}
                className={`text-gray-600 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-300 ${
                  isActive(path)
                    ? "text-green-600 dark:text-green-400 font-medium"
                    : ""
                }`}
              >
                {path === "/"
                  ? "Home"
                  : path.slice(1).charAt(0).toUpperCase() + path.slice(2)}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={cycleTheme}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
              aria-label="Toggle Theme"
            >
              {theme === "light" && <Sun className="w-5 h-5 text-yellow-500" />}
              {theme === "dark" && <Moon className="w-5 h-5 text-blue-500" />}
              {theme === "system" && (
                <Monitor className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            {isOnOrganizerPortal && isOrganizerAuth ? (
              <Button 
                variant="outline" 
                onClick={() => {
                  dispatch(logoutOrganizer());
                  navigate("/organizer/login");
                }}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 border-gray-200 dark:border-gray-700"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden lg:inline">Logout</span>
              </Button>
            ) : isOnSponsorPortal && isSponsorAuth ? (
              <Button 
                variant="outline" 
                onClick={() => {
                  dispatch(logoutSponsor());
                  navigate("/sponsor/login");
                }}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 border-gray-200 dark:border-gray-700"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden lg:inline">Logout</span>
              </Button>
            ) : isUserAuth && !isOnOrganizerPortal && !isOnSponsorPortal ? (
              // On regular pages: show UserDropdown for logged-in users
              <UserDropdown />
            ) : isOrganizerAuth && !isOnOrganizerPortal ? (
              <Link to="/organizer/dashboard">
                <Button variant="primary">Organizer Portal</Button>
              </Link>
            ) : isSponsorAuth && !isOnSponsorPortal ? (
              <Link to="/sponsor/dashboard">
                <Button variant="primary">Sponsor Portal</Button>
              </Link>
            ) : !isUserAuth && !isOrganizerAuth && !isSponsorAuth ? (
              // Show login/register buttons ONLY if no one is authenticated
              <>
                <Link
                  to="/login"
                  className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300"
                >
                  Login
                </Link>
                <Button variant="primary" onClick={() => navigate("/register")}>
                  Register
                </Button>
              </>
            ) : null}
          </div>

          {/* Mobile Actions (Theme Toggle + Menu Button) */}
          <div className="flex items-center space-x-2 md:hidden">
            <button
              onClick={cycleTheme}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
            >
              {theme === "light" && <Sun className="w-5 h-5 text-yellow-500" />}
              {theme === "dark" && <Moon className="w-5 h-5 text-blue-500" />}
              {theme === "system" && (
                <Monitor className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden py-4 border-t border-gray-200"
          >
            <nav className="flex flex-col space-y-4">
              <Link
                to="/"
                className={`text-gray-600 hover:text-emerald-500 transition-colors duration-300 ${
                  isActive("/") ? "text-emerald-600 font-medium" : ""
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>

              <Link
                to="/events"
                className={`text-gray-600 hover:text-emerald-500 transition-colors duration-300 ${
                  isActive("/events") ? "text-emerald-600 font-medium" : ""
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Events
              </Link>
              <Link
                to="/leaderboard"
                className="block text-gray-600 hover:text-emerald-500 transition-colors duration-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Leaderboard
              </Link>
              <Link
                to="/about"
                className={`text-gray-600 hover:text-emerald-500 transition-colors duration-300 ${
                  isActive("/about") ? "text-emerald-600 font-medium" : ""
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>

              {/* Mobile Auth Section */}
              <div className="pt-4 border-t border-gray-200">
                {isOnOrganizerPortal && isOrganizerAuth ? (
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full justify-center flex items-center gap-2"
                      onClick={() => {
                        dispatch(logoutOrganizer());
                        navigate("/organizer/login");
                        setIsMenuOpen(false);
                      }}
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </Button>
                  </div>
                ) : isOnSponsorPortal && isSponsorAuth ? (
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full justify-center flex items-center gap-2"
                      onClick={() => {
                        dispatch(logoutSponsor());
                        navigate("/sponsor/login");
                        setIsMenuOpen(false);
                      }}
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </Button>
                  </div>
                ) : isUserAuth && !isOnOrganizerPortal && !isOnSponsorPortal ? (
                  // On regular pages: show UserDropdown for logged-in users
                  <div className="space-y-3">
                    <div className="text-sm text-gray-500">Signed in as:</div>
                    <UserDropdown />
                  </div>
                ) : isOrganizerAuth && !isOnOrganizerPortal ? (
                  <div className="space-y-3">
                    <Link
                      to="/organizer/dashboard"
                      className="block w-full"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Button variant="primary" className="w-full">
                        Organizer Portal
                      </Button>
                    </Link>
                  </div>
                ) : isSponsorAuth && !isOnSponsorPortal ? (
                  <div className="space-y-3">
                    <Link
                      to="/sponsor/dashboard"
                      className="block w-full"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Button variant="primary" className="w-full">
                        Sponsor Portal
                      </Button>
                    </Link>
                  </div>
                ) : !isUserAuth && !isOrganizerAuth && !isSponsorAuth ? (
                  <div className="space-y-3">
                    <Link
                      to="/login"
                      className="block text-gray-600 hover:text-emerald-500 transition-colors duration-300"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Button
                      variant="primary"
                      className="w-full text-center"
                      onClick={() => {
                        navigate("/register");
                        setIsMenuOpen(false);
                      }}
                    >
                      Register
                    </Button>
                  </div>
                ) : null}
              </div>
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
};

export default Header;
